import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useUserStore } from '@/stores/user';

const GET_COMPANY_QUERY = gql`
  query getCurrentUser {
    currentUser {
      company {
        integrations
        invoiceFooter
        invoiceFooterThermal
        invoiceHeader
        invoiceImage
        invoiceRecipientTemplate
        medical
        receiptHeader
        paymentGatewayProvider {
          key
        }
        settings {
          sales {
            generateInvoiceAutomatically
            invoiceDueDateInDays
            paymentLink
            printNextAppointment
            showTreatmentDateOnInvoice
            showVatOnInvoice
            thermalSupport
          }
          datev {
            bankAccount
            billsReceivableAccount
            cashAccount
            deferredRevenueAccount
            revenueAccount
          }
        }
      }
    }
  }
`;

// Provided to the settings sections and passed to the preview, so they share one shape
// rather than each injecting `any`. This whole object is sent verbatim as the
// updateCompany mutation input, so it must only ever hold settable, saveable fields —
// `medical` is a read-only, computed company property and is exposed separately instead.
export interface InvoiceFormData {
  invoiceHeader: string;
  invoiceFooter: string;
  invoiceFooterThermal: string;
  invoiceRecipientTemplate: string;
  invoiceImage: string;
  receiptHeader: string;
  settings: {
    sales: {
      generateInvoiceAutomatically: boolean;
      invoiceDueDateInDays: number;
      paymentLink: boolean;
      printNextAppointment: boolean;
      showTreatmentDateOnInvoice: boolean;
      showVatOnInvoice: boolean;
      thermalSupport: boolean;
    };
    datev: {
      bankAccount: number;
      billsReceivableAccount: number;
      cashAccount: number;
      deferredRevenueAccount: number;
      revenueAccount: number;
    };
  };
}

export const useInvoices = () => {
  const { onResult, loading } = useQuery(GET_COMPANY_QUERY);
  const { hasFeatureFlag } = useUserStore();
  const hasPaymentProvider = ref(false);
  const hasPaymentLink = ref(false);
  const isMedical = ref(false);

  const currentImageUrl = ref('');
  const formData: InvoiceFormData = reactive({
    invoiceHeader: '',
    invoiceFooter: '',
    invoiceFooterThermal: '',
    invoiceRecipientTemplate: '',
    invoiceImage: '',
    receiptHeader: '',
    settings: {
      sales: {
        generateInvoiceAutomatically: false,
        invoiceDueDateInDays: 14,
        paymentLink: false,
        printNextAppointment: false,
        showTreatmentDateOnInvoice: false,
        showVatOnInvoice: false,
        thermalSupport: false,
      },
      datev: {
        bankAccount: 0,
        billsReceivableAccount: 0,
        cashAccount: 0,
        deferredRevenueAccount: 0,
        revenueAccount: 0,
      },
    },
  });

  onResult(({ data: { currentUser } }) => {
    formData.invoiceHeader = currentUser.company.invoiceHeader;
    formData.invoiceFooter = currentUser.company.invoiceFooter;
    formData.invoiceRecipientTemplate =
      currentUser.company.invoiceRecipientTemplate;

    // The receipt fields only exist in the compliant-register settings form, where they
    // are prefilled from their invoice counterparts so a company that never set them
    // keeps showing the same content on receipts as before (which is also what the
    // backend falls back to). Editing either one from then on keeps them independent.
    const receiptField = (
      receiptValue: string | null,
      invoiceValue: string | null,
    ) =>
      (hasFeatureFlag('compliant-register')
        ? receiptValue || invoiceValue
        : receiptValue) ?? '';

    formData.receiptHeader = receiptField(
      currentUser.company.receiptHeader,
      currentUser.company.invoiceHeader,
    );
    formData.invoiceFooterThermal = receiptField(
      currentUser.company.invoiceFooterThermal,
      currentUser.company.invoiceFooter,
    );

    formData.invoiceImage = currentUser.company.invoiceImage;
    isMedical.value = currentUser.company.medical;
    formData.settings.sales.generateInvoiceAutomatically =
      currentUser.company.settings.sales.generateInvoiceAutomatically;
    formData.settings.sales.invoiceDueDateInDays =
      currentUser.company.settings.sales.invoiceDueDateInDays;
    formData.settings.sales.paymentLink =
      currentUser.company.settings.sales.paymentLink;
    formData.settings.sales.printNextAppointment =
      currentUser.company.settings.sales.printNextAppointment;
    formData.settings.sales.showTreatmentDateOnInvoice =
      currentUser.company.settings.sales.showTreatmentDateOnInvoice;
    formData.settings.sales.showVatOnInvoice =
      currentUser.company.settings.sales.showVatOnInvoice;
    formData.settings.sales.thermalSupport =
      currentUser.company.settings.sales.thermalSupport;
    formData.settings.datev.bankAccount =
      currentUser.company.settings.datev.bankAccount;
    formData.settings.datev.billsReceivableAccount =
      currentUser.company.settings.datev.billsReceivableAccount;
    formData.settings.datev.cashAccount =
      currentUser.company.settings.datev.cashAccount;
    formData.settings.datev.deferredRevenueAccount =
      currentUser.company.settings.datev.deferredRevenueAccount;
    formData.settings.datev.revenueAccount =
      currentUser.company.settings.datev.revenueAccount;

    currentImageUrl.value = currentUser.company.invoiceImage;

    hasPaymentProvider.value =
      !!currentUser.company.paymentGatewayProvider?.key;

    hasPaymentLink.value =
      currentUser.company.paymentGatewayProvider?.key === 'stripe' ||
      currentUser.company.paymentGatewayProvider?.key === 'mollie';
  });

  return {
    formData,
    currentImageUrl,
    hasPaymentProvider,
    hasPaymentLink,
    isMedical,
    loading,
  };
};
